import React, { useEffect, useState } from "react";
import API_URL from "./config";
import "./TradeModal.css"; // Reuse modal styling

export default function RecoverModal({ roster, saveId, teamId, stamina, onClose, onRecoverComplete }) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user._id;

  // Get list of injured players
  const injuredPlayers = roster.filter((p) => p.injuryType).sort((a, b) => a.name?.localeCompare(b.name || "") || 0);

  const handleRecover = async () => {
    if (!selectedPlayer) {
      setError("Please select a player");
      return;
    }

    if (stamina < 1) {
      setError("Not enough stamina (need 1)");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(
        `${API_URL}/api/players/${selectedPlayer._id}/recover-injury?save_id=${saveId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-ID": userId,
          },
          body: JSON.stringify({ save_id: saveId }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Recovery failed");
      }

      const data = await res.json();
      setResult({
        playerName: data.playerName,
        injuryType: selectedPlayer.injuryType,
        remainingStamina: data.remainingStamina,
      });
    } catch (err) {
      setError(err.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (result && onRecoverComplete) {
      onRecoverComplete(result);
    }
    onClose();
  };

  return (
    <div className="trade-overlay" onClick={handleClose}>
      <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="trade-modal-header">
          <h2 className="trade-modal-title">Recover Injured Player</h2>
          <button className="trade-close-btn" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="trade-modal-content" style={{ padding: "2rem", flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          {result ? (
            // Success state
            <div style={{ textAlign: "center", padding: "1rem 0" }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>💚</div>
              <p style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.5rem", color: "var(--color-text-main)" }}>
                {result.playerName}'s injury recovered!
              </p>
              <p style={{ fontSize: "0.95rem", color: "var(--color-text-secondary)", marginBottom: "1.5rem", lineHeight: "1.6" }}>
                {result.playerName} is no longer dealing with their {result.injuryType}. They're ready to compete again!
              </p>
              <p style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "1.5rem" }}>
                <strong style={{ color: "var(--color-text-main)" }}>Stamina remaining:</strong> {result.remainingStamina}/3
              </p>
              <button
                onClick={handleClose}
                style={{
                  padding: "0.875rem 1.5rem",
                  background: "var(--color-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  minWidth: "140px"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "var(--color-secondary)";
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(18, 82, 210, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "var(--color-primary)";
                  e.target.style.transform = "none";
                  e.target.style.boxShadow = "none";
                }}
              >
                Back to Team
              </button>
            </div>
          ) : (
            <>
              {/* Injured Players Section */}
              <div style={{ marginBottom: "1.5rem" }}>
                <h3 style={{ 
                  fontSize: "0.85rem", 
                  fontWeight: 700, 
                  marginBottom: "1rem", 
                  textTransform: "uppercase", 
                  letterSpacing: "0.6px", 
                  color: "var(--color-text-secondary)" 
                }}>
                  Injured Players ({injuredPlayers.length})
                </h3>
                
                {injuredPlayers.length === 0 ? (
                  <div style={{ 
                    padding: "2rem 1rem", 
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.03)",
                    borderRadius: "8px",
                    border: "1px solid rgba(255, 255, 255, 0.05)"
                  }}>
                    <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem" }}>
                      No injured players on your roster.
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      maxHeight: "350px",
                      overflowY: "auto",
                      paddingRight: "4px"
                    }}
                  >
                    {injuredPlayers.map((player) => (
                      <button
                        key={player._id}
                        onClick={() => setSelectedPlayer(player)}
                        style={{
                          padding: "1rem",
                          border: selectedPlayer?._id === player._id ? "2px solid var(--color-primary)" : "1px solid rgba(255, 255, 255, 0.1)",
                          background:
                            selectedPlayer?._id === player._id
                              ? "rgba(18, 82, 210, 0.15)"
                              : "rgba(255, 255, 255, 0.05)",
                          borderRadius: "8px",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease",
                          color: "var(--color-text-main)",
                          hover: {
                            background: "rgba(255, 255, 255, 0.08)"
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (selectedPlayer?._id !== player._id) {
                            e.target.style.background = "rgba(255, 255, 255, 0.08)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedPlayer?._id !== player._id) {
                            e.target.style.background = "rgba(255, 255, 255, 0.05)";
                          }
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: "0.4rem", fontSize: "1rem" }}>
                          {player.name}
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>
                          <span style={{ color: "#ef4444", fontWeight: 500 }}>{player.injuryType}</span> • Out {player.injuryGamesRemaining} game{player.injuryGamesRemaining !== 1 ? "s" : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Stamina Bar */}
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
                  <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", minWidth: "80px" }}>Stamina</span>
                  <div style={{ display: "flex", gap: "0.5rem", flex: 1 }}>
                    {[0, 1, 2].map((index) => {
                      let bgColor, shadow, borderColor, animation;
                      if (index < stamina - 1) {
                        bgColor = "#4caf50";
                        shadow = "0 0 12px rgba(76, 175, 80, 0.4)";
                        borderColor = "#4caf50";
                        animation = "none";
                      } else if (index === stamina - 1 && stamina > 0) {
                        bgColor = "#fbbf24";
                        shadow = "0 0 12px rgba(251, 191, 36, 0.6)";
                        borderColor = "#fbbf24";
                        animation = "staminaPulse 2s ease-in-out infinite";
                      } else {
                        bgColor = "rgba(76, 175, 80, 0.15)";
                        shadow = "none";
                        borderColor = "#374151";
                        animation = "none";
                      }
                      return (
                        <div
                          key={index}
                          style={{
                            flex: 1,
                            height: "20px",
                            borderRadius: "4px",
                            transition: "all 0.3s ease",
                            border: `2px solid ${borderColor}`,
                            background: bgColor,
                            boxShadow: shadow,
                            animation: animation
                          }}
                        />
                      );
                    })}
                  </div>
                  <span style={{ fontSize: "0.95rem", fontWeight: "700", color: stamina < 1 ? "#f87171" : "#fff", minWidth: "45px", textAlign: "right" }}>{stamina}/3</span>
                </div>
                <div style={{ color: "var(--color-text-secondary)", fontSize: "0.85rem", lineHeight: "1.5" }}>
                  Spend 1 stamina to instantly heal a player's injury. They'll be ready to play immediately.
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div style={{ 
                  background: "rgba(239, 68, 68, 0.1)", 
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#fca5a5", 
                  fontSize: "0.9rem", 
                  padding: "0.75rem",
                  borderRadius: "6px",
                  marginBottom: "1rem",
                  textAlign: "center"
                }}>
                  {error}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={handleClose}
                  style={{
                    flex: 1,
                    padding: "0.875rem 1.5rem",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    background: "transparent",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: "var(--color-text-secondary)",
                    fontWeight: 600,
                    fontSize: "1rem",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(18, 82, 210, 0.1)";
                    e.target.style.borderColor = "var(--color-primary)";
                    e.target.style.color = "var(--color-text-main)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                    e.target.style.color = "var(--color-text-secondary)";
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecover}
                  disabled={!selectedPlayer || stamina < 1 || loading}
                  style={{
                    flex: 1,
                    padding: "0.875rem 1.5rem",
                    background: !selectedPlayer || stamina < 1 || loading ? "rgba(18, 82, 210, 0.3)" : "var(--color-primary)",
                    color: "white",
                    border: "2px solid transparent",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1rem",
                    cursor: !selectedPlayer || stamina < 1 || loading ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    opacity: !selectedPlayer || stamina < 1 || loading ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!(!selectedPlayer || stamina < 1 || loading)) {
                      e.target.style.background = "var(--color-secondary)";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 4px 12px rgba(18, 82, 210, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(!selectedPlayer || stamina < 1 || loading)) {
                      e.target.style.background = "var(--color-primary)";
                      e.target.style.transform = "none";
                      e.target.style.boxShadow = "none";
                    }
                  }}
                >
                  {loading ? "Recovering..." : "Recover (1 Stamina)"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
